/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/schedule/QuickEntryModal.tsx
 * PURPOSE: Modal [5] "Nhập nhanh bằng văn bản" (BR-SCHD-011, 🆕 CHƯA CÓ BR
 *          chính thức — wireframe là nguồn đầu tiên) — SC-009-SCHEDULE-GRID.
 *          Textarea + khoảng áp dụng + xem trước (debounce ~300ms, parse
 *          PHÍA TRÌNH DUYỆT qua quickEntryParser) + nút "Gán N dòng hợp lệ".
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-14
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — Nhập ca hàng loạt bằng văn bản (dọn đường cho lên-lịch
 *   qua-chat sau này — tách đọc-hiểu-chuỗi (FE) khỏi ghi-lịch (BE))
 * - Impact: Operations — kênh nhập nhanh cho quản lý quen gõ/dán từ bảng tính
 * - Stakeholders: Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer (modal, textarea + preview list)
 * - Pattern: parse-on-client (quickEntryParser, debounce 300ms) → POST
 *   assignments đã parse (useQuickEntrySchedule) — KHÔNG gửi text thô lên BE
 * - Dependencies: quickEntryParser, useQuickEntrySchedule, DateRangePicker
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §SC-009-SCHEDULE-GRID ANNOTATION "[5] Nhập nhanh bằng văn bản" (dòng ~3427)
 *   + GHI CHÚ THEO STATE "Nhập nhanh" (dòng ~3567-3572) + STATE table
 *   (dòng ~3668-3680) + NHÃN VI (dòng ~3712)
 * - Backend: backend/app/api/v2/endpoints/manager_schedule.py (quick_entry_schedule)
 * - Parser: frontend/src/pages/manager/schedule/quickEntryParser.ts
 * ===============================================================================
 */

import React, { useEffect, useMemo, useState } from "react";
import { useQuickEntrySchedule } from "../../../hooks/useManagerSchedule";
import type { QuickEntryResult, ShiftCodeItem } from "../../../services/api/managerApi";
import { DateRangePicker } from "../../../components/ui/DateRangePicker";
import { extractApiError, formatWeekRange } from "./scheduleShared";
import {
  parseQuickEntryText,
  type QuickEntryRosterEmployee,
  type QuickEntryParseResult,
} from "./quickEntryParser";
import styles from "../ManagerSchedulePage.module.css";

/** Cùng trần [2e] (phạm vi GÁN) — khớp backend span_days > 62 → 422 */
const MAX_QUICK_ENTRY_SPAN_DAYS = 62;
const DEBOUNCE_MS = 300;

const CloseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} width={22} height={22} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

function toISODateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function enumerateDatesISO(startISO: string, endISO: string): string[] {
  const out: string[] = [];
  const cursor = new Date(`${startISO}T00:00:00`);
  const end = new Date(`${endISO}T00:00:00`);
  while (cursor.getTime() <= end.getTime()) {
    out.push(toISODateLocal(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

interface QuickEntryModalProps {
  /** Roster của lưới ĐANG MỞ — mọi dữ liệu tra cứu đã có sẵn phía trình duyệt */
  employees: QuickEntryRosterEmployee[];
  shiftCodes: ShiftCodeItem[];
  /** Khoảng áp dụng mặc định = khoảng đang chọn trên thanh — đổi được ngay trong hộp */
  defaultDateStart: string;
  defaultDateEnd: string;
  onClose: () => void;
  onToast: (message: string) => void;
}

const QuickEntryModal: React.FC<QuickEntryModalProps> = ({
  employees,
  shiftCodes,
  defaultDateStart,
  defaultDateEnd,
  onClose,
  onToast,
}) => {
  const [rangeStart, setRangeStart] = useState(defaultDateStart);
  const [rangeEnd, setRangeEnd] = useState(defaultDateEnd);
  const [text, setText] = useState("");
  const [debouncedText, setDebouncedText] = useState("");
  const [apiError, setApiError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const mutation = useQuickEntrySchedule();

  // Kiểm liên tục — gõ/dán → chờ ~300ms rồi phân tích lại toàn bộ (GHI CHÚ
  // THEO STATE "Nhập nhanh": "cố ý không có trạng thái đang-tải riêng — mọi
  // dữ liệu tra cứu đã có sẵn phía trình duyệt").
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedText(text), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const dates = useMemo(() => {
    if (rangeEnd < rangeStart) return [];
    return enumerateDatesISO(rangeStart, rangeEnd);
  }, [rangeStart, rangeEnd]);

  const spanTooLong = dates.length > MAX_QUICK_ENTRY_SPAN_DAYS;

  const todayISO = useMemo(() => toISODateLocal(new Date()), []);
  // "Khoảng khoá — khoảng chọn trong hộp nằm trọn trong quá khứ ... khoá
  // toàn bộ ô nhập + banner ngay khi mở/đổi khoảng; vẫn cho đổi sang khoảng
  // khác." (chưa phủ nhánh "đã xuất bản mà vai mất quyền ghi" — cần thêm
  // prop trạng thái publish/quyền của tuần đang xem để phủ đủ, ngoài phạm
  // vi lượt này; ghi rõ trong báo cáo).
  const isRangeLocked = rangeEnd < todayISO;

  const parseResult: QuickEntryParseResult = useMemo(() => {
    if (isRangeLocked || spanTooLong || dates.length === 0) {
      return { lines: [], validLineCount: 0, errorLineCount: 0, assignments: [], assignedCount: 0, summary: "" };
    }
    return parseQuickEntryText(debouncedText, { employees, shiftCodes, dates });
  }, [debouncedText, employees, shiftCodes, dates, isRangeLocked, spanTooLong]);

  const hasUnsavedContent = text.trim().length > 0;

  const requestClose = () => {
    if (hasUnsavedContent) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  const handleSubmit = () => {
    if (mutation.isPending || parseResult.assignments.length === 0) return;
    setApiError(null);
    // Số DÒNG áp dụng thật (loại lỗi/trùng-bị-thay) — dùng cho toast "Đã lưu
    // {N} dòng ({M} phân ca)" theo đúng NHÃN VI, tính TRƯỚC khi biết skipped
    // backend (skipped chỉ phá vỡ M, không đổi N dòng đã cố gửi).
    const appliedLines = parseResult.lines.filter((l) => !l.error && !l.supersededByLaterDuplicate);
    mutation.mutate(
      { date_start: rangeStart, date_end: rangeEnd, assignments: parseResult.assignments },
      {
        onSuccess: (result: QuickEntryResult) => {
          const rangeLabel = formatWeekRange(rangeStart, rangeEnd);
          const skippedPart =
            result.skipped.length > 0
              ? ` — bỏ qua ${result.skipped.length} giá trị (trùng ca/vi phạm ràng buộc/hết hạn)`
              : "";
          onToast(`Đã lưu ${appliedLines.length} dòng (${result.assigned_count} phân ca) ở ${rangeLabel}${skippedPart}`);

          // "Gán xong — dòng đã gán biến mất khỏi ô dán; hộp KHÔNG tự đóng để
          // còn dán tiếp." Chỉ xoá dòng ÁP DỤNG TRỌN VẸN (không lỗi, không bị
          // dòng sau thay thế, VÀ không có (user_id,date) nào của dòng đó rơi
          // vào `skipped` backend trả về — "Gán lỗi — không xoá dòng nào").
          const skippedPairs = new Set(result.skipped.map((s) => `${s.user_id}:${s.date}`));
          const remainingLines = parseResult.lines.filter((line) => {
            if (line.error) return true; // giữ nguyên để sửa
            if (line.supersededByLaterDuplicate) return true; // còn hiện để người dùng thấy bị thay
            if (!line.matchedEmployee) return true;
            const touchedByThisLine = line.values.some(
              (v) => !v.isOff && skippedPairs.has(`${line.matchedEmployee!.user_id}:${v.dateISO}`),
            );
            return touchedByThisLine; // còn phần bị skip → giữ lại để xem lý do
          });
          setText(remainingLines.map((l) => l.raw).join("\n"));
        },
        onError: (error) => {
          // "Lưu thất bại → ô quay về nguyên trạng trước thao tác + thông báo
          // nổi 'Không lưu được · Thử lại'. Không giữ thay đổi trên màn khi
          // máy chủ chưa nhận" — ở đây nghĩa là KHÔNG xoá nội dung ô dán.
          setApiError(extractApiError(error).message || "Không lưu được · Thử lại");
        },
      },
    );
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${styles.modalWide}`} role="dialog" aria-modal="true" aria-labelledby="quickEntryTitle">
        <div className={styles.modalHead}>
          <h2 className={styles.modalTitle} id="quickEntryTitle">
            Nhập nhanh — Gán ca hàng loạt
          </h2>
          <button type="button" className={styles.btnIcon} aria-label="Đóng" onClick={requestClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.modalBody}>
          {apiError && (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginBottom: "var(--nqh-space-3)" }}>
              {apiError}
            </div>
          )}

          {confirmClose && (
            <div className={`${styles.banner} ${styles.bannerWarning}`} role="alertdialog" style={{ marginBottom: "var(--nqh-space-3)" }}>
              <span>Đóng và bỏ nội dung chưa gán?</span>
              <span style={{ marginLeft: "auto", display: "flex", gap: "var(--nqh-space-2)" }}>
                <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setConfirmClose(false)}>
                  Huỷ
                </button>
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onClose}>
                  Đóng, bỏ nội dung
                </button>
              </span>
            </div>
          )}

          <div className={styles.popoverLabel}>Khoảng áp dụng</div>
          <DateRangePicker
            value={{ start: rangeStart, end: rangeEnd }}
            onChange={(range) => {
              setRangeStart(range.start ?? defaultDateStart);
              setRangeEnd(range.end ?? range.start ?? defaultDateStart);
            }}
            maxSpanDays={MAX_QUICK_ENTRY_SPAN_DAYS}
            hidePresets
          />

          {isRangeLocked && (
            <div className={`${styles.banner} ${styles.bannerLock}`} role="status" style={{ marginTop: "var(--nqh-space-3)" }}>
              Khoảng này đã khoá — không gán được qua Nhập nhanh. Chọn khoảng khác.
            </div>
          )}
          {!isRangeLocked && spanTooLong && (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert" style={{ marginTop: "var(--nqh-space-3)" }}>
              Khoảng ngày tối đa {MAX_QUICK_ENTRY_SPAN_DAYS} ngày.
            </div>
          )}

          <div className={styles.popoverLabel} style={{ marginTop: "var(--nqh-space-3)" }}>
            Danh sách gán ca
          </div>
          <textarea
            className={styles.comboInput}
            rows={8}
            style={{ width: "100%", fontFamily: "monospace" }}
            value={text}
            disabled={isRangeLocked || spanTooLong}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Gõ hoặc dán — mỗi dòng 1 nhân viên, đúng ${dates.length || "N"} giá trị theo thứ tự ngày của khoảng đã chọn, cách nhau dấu phẩy. Ví dụ (khoảng 3 ngày):
NV001: M6, A2, M6
NV002: M6, OFF, A2
NV003: M6+A2, OFF, A2
OFF = không có ca · dấu + nối 2 mã ca cùng ngày (nhiều ca 1 ngày)`}
          />

          {!isRangeLocked && !spanTooLong && debouncedText.trim().length > 0 && (
            <>
              <p className={styles.importSummary}>{parseResult.summary}</p>
              <ul className={styles.importErrors}>
                {parseResult.lines.map((line) => {
                  if (line.error) {
                    return (
                      <li key={line.lineNumber}>
                        ✖ Dòng {line.lineNumber} · {line.raw} — {line.error}
                      </li>
                    );
                  }
                  const excludedNotes: string[] = [];
                  line.values.forEach((v) => {
                    v.codes.forEach((c) => {
                      if (c.excludedReason === "past_r42") excludedNotes.push(`${c.codeRaw} (Ngày đã qua)`);
                      if (c.excludedReason === "unknown_code") excludedNotes.push(`${c.codeRaw} (Mã ca không có trong danh mục)`);
                    });
                  });
                  const supersededNote = line.supersededByLaterDuplicate ? " — bị thay bởi dòng bên dưới" : "";
                  const excludedNote = excludedNotes.length > 0 ? ` — loại: ${excludedNotes.join(", ")}` : "";
                  return (
                    <li key={line.lineNumber}>
                      ✔ Dòng {line.lineNumber} · {line.matchedEmployee?.full_name}
                      {supersededNote}
                      {excludedNote}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        <div className={styles.modalFoot}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={requestClose}>
            Hủy
          </button>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            disabled={
              isRangeLocked ||
              spanTooLong ||
              parseResult.assignments.length === 0 ||
              mutation.isPending
            }
            onClick={handleSubmit}
          >
            {mutation.isPending && <span className={styles.spinner} aria-hidden="true" />}
            Gán {parseResult.validLineCount} dòng hợp lệ
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickEntryModal;
